import React from 'react'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import { EntityDraftViewModel } from '../../../interfaces/EntityDraftViewModel'
import { IRequirementViewModel } from '../../../interfaces/RequirementsInScopeModel'
import { isEmpty } from '../../../utils/common-utils'
import CONSTANTS from '../../../utils/constants'


export const getFormattedDate = (year, selectedMM) => {
  const month = new Date(Date.parse(`${selectedMM} 1, 2012`)).getMonth() + 1
  const day = new Date(year, month, 0).getDate()
  const formattedMonth = month.toString().padStart(2, '0')
  const formattedDate = `${year}-${formattedMonth}-${day}`

  return formattedDate;
}

export const filterTimelineEvents = (timelineEvents: any[], reportingMonth) => {
  const filteredEvents = timelineEvents.filter((item: any) => {
    const itemReportingMonth = removeTimestamp(item.reporting_month)

    return itemReportingMonth === reportingMonth;
  })
  return filteredEvents;
}

export const removeTimestamp = (date: String) => {
  return date.includes("T") ? date.split("T")[0] : date;
}

export const getFormInitialValuesFromEntityResponse = (entity) => {
  var result = {};

  if (!entity)
    return result;

  const entityProps = entity['properties'];

  Object.keys(entityProps).map((propKey) => {
    const entityProp = entityProps[propKey];
    const entityDgProps = entityProp['properties'];
    const entityDgCollectionProps = entityProp['collections'];
    if (entityDgProps && Object.entries(entityDgProps).length > 0) {
      // Check if Data Group
      getDataGroupPropsDisplay(entityProp, propKey, result, entityDgProps);
    } else if (entityDgCollectionProps && Object.entries(entityDgCollectionProps).length > 0) {
      // Check if Data Group - multiple cardinality
      getDataGroupCollectionPropsDisplay(entityProp, propKey, result, entityDgCollectionProps)
    } else if (entityProp.value) {
      result[propKey] = getFieldValueToDisplay(entityProp.value);
    }
    return null;
  });
  return result;
};

export const getDataGroupPropsDisplay = (entityProp, propKey, result, entityDgProps) => {
  const groupId = entityProp.customTypeId;
  for (const [key, field] of Object.entries(entityDgProps)) {
    if (field['value'])
      result[`dataGroup_${groupId}_${propKey}_false_${key}`] = getFieldValueToDisplay(field['value']);
  }
}

export const getDataGroupCollectionPropsDisplay = (entityProp, propKey, result, entityDgCollectionProps) => {
  const groupId = entityProp.dataGroupId;
  const dataGroupFieldKey = `dataGroup_${groupId}_${propKey}_true`;
  result[dataGroupFieldKey] = [];
  Object.values(entityDgCollectionProps).forEach((obj, index) => {
    if (obj && obj['properties']) {
      result[dataGroupFieldKey].push({});
      for (const [key, field] of Object.entries(obj['properties'])) {
        result[dataGroupFieldKey][index][key] = getFieldValueToDisplay(field);
      }
    }
  });
}

export const getFormDataValuesObject = (values) => {
  const formDataValues = {};

  if (values)
    Object.keys(values).forEach((field) => {
      // The if condition should check if the field is boolean as well
      // If only values[field] is checked, then a field that is boolean 
      // with value false will be skipped
      if (values[field] || typeof values[field] === 'boolean') {
        formDataValues[field] = getFieldValue(values[field]);
      } else if (typeof values[field] === 'string' && values[field].trim().length === 0) {
        formDataValues[field] = values[field];
      } else {
        formDataValues[field] = null;
      }
    });
  return formDataValues;
};

export const getFormDataValuesObjectForDraftApi = (targetEntity, values, jurisdictions, entityType = 'Company', categories?: string[]) => {
  const formData = {
    entityType: entityType,
    category: categories,
    jurisdictions: jurisdictions,
    targetEntity: targetEntity,
    properties: {}
  };
  return updateFormDataValuesObject(formData, values);
}

export const getFormDataValuesObjectForEntityApi = (targetEntity, values, jurisdictions, entityType) => {
  const jurisdictionNames = jurisdictions.map(jurisdiction => jurisdiction.jurisdiction);

  const formData = {
    type: entityType,
    policyJurisdictions: jurisdictionNames,
    parentPolicyJurisdictions: jurisdictions,
    targetEntity: targetEntity,
    properties: {}
  };
  return updateFormDataValuesObject(formData, values);
}

export const updateFormDataValuesObject = (formData, values) => {

  const formDataValues = getFormDataValuesObject(values);

  const dataGroups = {};

  const dataGroupFields = Object.keys(values).filter(key => key.includes('dataGroup_'));

  dataGroupFields.forEach(field => {
    // TODO
    const fieldNames = field.split('_');
    const groupId = fieldNames[1];
    const groupName = fieldNames[2];
    const cardinality = fieldNames[3];
    const propertyName = fieldNames[4];

    if (!dataGroups.hasOwnProperty(groupName))
      dataGroups[groupName] = { groupId, cardinality, properties: {} };

    if (cardinality === "true") {
      dataGroups[groupName]['collections'] = formDataValues[field];
    } else if (cardinality === "false") {
      dataGroups[groupName]['properties'][propertyName] = {
        value: formDataValues[field],
        type: 'Single',
        properties: {}
      };
    }
  });

  Object.keys(dataGroups).forEach((dataGroup) => {
    if (dataGroups[dataGroup]['cardinality'] === "true") {
      formData.properties[dataGroup] = {
        dataGroupId: dataGroups[dataGroup]['groupId'],
        type: 'Collection',
        collections: dataGroups[dataGroup]['collections']
      };
    } else if (dataGroups[dataGroup]['cardinality'] === "false") {
      formData.properties[dataGroup] = {
        customTypeId: dataGroups[dataGroup]['groupId'],
        type: 'Custom',
        properties: dataGroups[dataGroup]['properties']
      };
    }
  });

  Object.keys(values).filter(key => !key.includes('dataGroup_')).forEach((field) => {
    if ((values[field] && formDataValues[field]) || typeof values[field] === 'boolean') {
      formData.properties[field] = {
        value: formDataValues[field],
        type: 'Single'
      };
    }
    // add empty string field
    if (typeof values[field] === 'string' && values[field].trim().length === 0) {
      formData.properties[field] = {
        value: formDataValues[field],
        type: 'Single'
      };
    }
  });

  return formData;
}

export const getDynamicReqsPayload = (initialValues, currentValues?: any, formValues?) => {
  const dynamicReqsPayload = {
    properties: {}
  };
  currentValues = Object.assign({}, initialValues, getFormDataValuesObject(formValues));
  updateFormDataValuesObject(dynamicReqsPayload, currentValues);
  return dynamicReqsPayload['properties'];
}

const createCollectionObj = (field) => {
  let collectionField = {};
  field.forEach(item => {
    const newUuid = uuidv4();
    const newItem = {};
    if (item)
      Object.keys(item).forEach((key) => {
        newItem[key] = getFieldValue(item[key]);
      });
    collectionField[newUuid] = { properties: newItem }
  });
  return collectionField;
}

export const getFieldValue = (field) => {
  if (field === true)
    return 'Yes';

  if (field === false)
    return 'No';

  if (!field)
    return;

  if (Array.isArray(field) && field.some(value => { return typeof value == 'object' })) {
    return createCollectionObj(field);
  }

  if (field && field !== ""
    && (
      moment(field, CONSTANTS.DATE_FORMAT_ISO_8601, true).isValid()
      || moment(field, CONSTANTS.DATE_FORMAT, true).isValid()
    )
  ) {
    return moment(field).format(CONSTANTS.DATE_FORMAT_ISO_8601);
  }

  if (!Array.isArray(field) && typeof field === 'object') {
    return field;
  }

  return field.toString();
}

const getFieldValueToDisplay = (value) => {
  // if (value === 'Yes')
  //   return true;
  // if (value === 'No')  
  //   return false;

  var isNumber = value && !isNaN(value) && !value.includes('T');

  // checking whether the field value is a valid date and then converting date to MM/DD/YYYY format
  const dateValue = moment(value, CONSTANTS.DATE_FORMAT_ISO_8601, true);
  if (value && !isNumber && dateValue.isValid()) {
    return dateValue;
  }

  return value;
}

export const parseClassificationAttribute = (classification) => {
  if (classification) {
    let classificationFields = {};
    const classificationData = classification.split('|');
    classificationData.forEach(cElement => {
      const cFields = cElement.split('::');
      if (cFields.length > 1)
        classificationFields[cFields[0].trim()] = cFields[1].trim();
    });
    return classificationFields;
  }

  return;
}

export const addAnotherDataGroup = (dataGroupFieldName, latestValues, setCurrentValues, formMethods, removedDataGroupIndexes, setRemovedDataGroupIndexes) => {
  if (!latestValues[dataGroupFieldName]) {
    latestValues[dataGroupFieldName] = [];
    latestValues = Object.assign({}, latestValues, formMethods.getValues());

    removedDataGroupIndexes[dataGroupFieldName] = [];
    setRemovedDataGroupIndexes(removedDataGroupIndexes);
  }
  latestValues[dataGroupFieldName].push({});
  setCurrentValues(latestValues);
};



export const canDisplayField = (field, formValues, dataGroupKey = null, groupIndex = 0) => {
  return true;
  //return !field.conditions || field.conditions.length === 0 || isFieldConditionsMet(field.conditions, formValues, dataGroupKey, groupIndex);
}

export const loadFormFields = (initialValues: any, formMethods: any, requirementsInScope: IRequirementViewModel[], setFieldsInPanel: any, setPanels: any) => {
  let tempFields = {};
  let tempPanels = [];
  const formValues = formMethods.getValues ? formMethods.getValues() : {}
  const values = Object.assign({}, initialValues, formValues);

  Object.values(requirementsInScope).forEach((field) => {
    if (canDisplayField(field, values)) {
      tempFields[field.dataField ? field.dataField.propertyName : field.documentDataKey] = field.category;

      if (!tempPanels.includes(field.category)) {
        tempPanels.push(field.category);
      }
    }
  });

  setFieldsInPanel(tempFields);
  setPanels(tempPanels);
}

export const showFieldLabel = (field, uiMetaData) => {
  return (
    (field.dataField?.propertyType !== 'dataGroup')
    && field.type !== 'Document'
    && (
      !uiMetaData
      || !uiMetaData.fieldType
      || (uiMetaData.fieldType && uiMetaData.fieldType !== 'checkboxes')
    )
    //&& (field.type !== 'Document')
  );
}

/**
 * Function to get the field value from the form values
 * @param getValues form current values
 * @param fieldName field name for which the value is to be fetched
 * @returns field value
 */
export const getFormFieldValue = (getValues, fieldName) => {
  return getValues() ? getValues()[fieldName] : '';
}

export const getDynamicDataGroupFieldsReqPayload = (
  latestValues,
  dataGroups,
  dataGroupId = null,
  dataGroupField = null
) => {
  const dynamicDataGroupRequests: any = [];

  if (isEmpty(latestValues)) return dynamicDataGroupRequests;

  Object.entries(latestValues).forEach(([fieldName, fieldValues]) => {
    if (fieldName.includes('dataGroup_') && (!dataGroupId || fieldName.includes(dataGroupId))) {
      const dataGroupKeys = fieldName.split('_');
      const currentDataGroupId = dataGroupKeys[1];
      const currentDataGroupFieldPropertyName = dataGroupKeys[2];
      const currentDataGroupName = dataGroups.find((dg: any) => dg.groupId === currentDataGroupId)?.name;

      if (
        !currentDataGroupName ||
        (dataGroupField &&
          currentDataGroupFieldPropertyName !==
          dataGroupField.dataField.propertyName)
      )
        return;

      // Skip API call if the props are empty
      if (
        !fieldValues ||
        Object.keys(fieldValues).length === 0 ||
        Object.values(fieldValues).every((val) => !val)
      )
        return;

      // TODO: Need to change it to support dynamic data group reqs for all collections in the data group
      for (const key in fieldValues as any[]) {
        const fieldValue = fieldValues[key];

        const { properties } = fieldValue;

        if (properties) {
          const currentDataGroupProps = {};

          for (const property in properties) {
            currentDataGroupProps[`${currentDataGroupName}.${property}`] =
              properties[property];
          }

          dynamicDataGroupRequests.push({
            groupId: currentDataGroupId,
            properties: currentDataGroupProps,
            fieldValueKey: key,
          });
        }
      }
    }
  });

  return dynamicDataGroupRequests;
};

export const containJurisdiction = (entityDraft: any, JurisdictionName: string) => {
  if (!entityDraft || Object.keys(entityDraft.jurisdictions).length === 0) {
    return false;
  }
  return entityDraft.jurisdictions.filter(Jurisdiction => Jurisdiction?.jurisdiction === JurisdictionName).length > 0;
}

export const getDefaultPropertyValue = () => {
  return {
    type: 'Single',
    value: null,
  }
}

export const isFixedHelpTooltip = (field) => {
  const uiMetaData: any = parseMetaDataAttribute(field?.classification);
  return (
    CONSTANTS.FIXED_HELP_TOOLTIP_COMPONENTS.indexOf(uiMetaData?.fieldType
      ? uiMetaData.fieldType
      : field?.dataField?.propertyType) > -1
  );
};

export const parseMetaDataAttribute = (metaData): any => {
  if (metaData) {
    const fields = {};
    const data = metaData.split('|');
    data.forEach((cElement) => {
      const cFields = cElement.split('::');
      if (cFields.length > 1) fields[cFields[0].trim()] = cFields[1].trim();
    });
    return fields;
  }

  return {};
};

export const getDataGroupExpirationDate = (lookups, dataGroupName, fieldName, entityDraft: any = null) => {
  let expirationDateFormat = null;
  if (lookups) {
    const dataGroupClassification = (lookups).find(lookup => lookup['lookupName'].trim() === 'Data Group Classification');
    if (dataGroupClassification && dataGroupClassification['values']) {
      dataGroupClassification.values.forEach(value => {
        const parseMetsData: any = parseMetaDataAttribute(value);
        if (entityDraft.jurisdictions && entityDraft.jurisdictions.some((ele) => ele.jurisdiction == CONSTANTS.UNITED_KINGDOM) &&
          dataGroupName.includes(parseMetsData.dataGroupName) && parseMetsData.fieldName === fieldName) {
          expirationDateFormat = value;
        }
      });
    }
  }
  return expirationDateFormat;
};

export const getTooltipDescription = (lookups, dataGroupName, fieldName) => {
  let tooltipDescription = '';
  if (lookups) {
    const tooltipClassification = lookups.find((lookup) => lookup['lookupName'].trim() === 'Data Group Tooltips');
    if (tooltipClassification && tooltipClassification['columns']) {
      const contentColumn = tooltipClassification['columns'].find((column) => column.name === 'Classification');
      if (contentColumn && contentColumn.values) {
        Object.keys(contentColumn.values).forEach((key) => {
          const parseMetsData: any = parseMetaDataAttribute(contentColumn.values[key]['value']);
          if (
            parseMetsData.dataGroupName === dataGroupName &&
            parseMetsData.fieldName === fieldName
          ) {
            tooltipDescription = key;
          }
        });
      }
    }
  }
  return tooltipDescription;
};

export const isOutlinedLabel = (field) => {
  const uiMetaData: any = parseMetaDataAttribute(field?.classification);
  return (
    CONSTANTS.OUTLINE_LABEL_COMPONENTS.indexOf(uiMetaData?.fieldType
      ? uiMetaData.fieldType
      : field?.dataField?.propertyType) > -1
  );
};

export const getValidFieldContentKey = (fieldType, contentKey, fieldName = '') => {
  if (fieldType !== 'dataGroup' || CONSTANTS.DATA_GROUP_FIELD_CONTENT.includes(fieldName)) {
    return contentKey;
  }
};



export const getDynamicProps = (entityDraft: EntityDraftViewModel) => {
  const dynamicProps = {};
  if (entityDraft) {
    for (const [propKey, propValue] of Object.entries(entityDraft.properties)) {
      dynamicProps[propKey] = isNaN(Number(propValue.value))
        ? propValue.value
        : Number(propValue.value);
    }
  }

  return dynamicProps;
};

export const removeDataGroupAndUpdateFormValues = (dataGroupFieldName, indexToRemove, removedDataGroupIndexes, setRemovedDataGroupIndexes, latestValues, setCurrentValues) => {
  const removedIndexes = removedDataGroupIndexes[dataGroupFieldName] || [];
  removedIndexes.push(Number(indexToRemove));
  removedDataGroupIndexes[dataGroupFieldName] = removedIndexes;

  latestValues[dataGroupFieldName][indexToRemove] = {};
  const nonEmptyObj = latestValues[dataGroupFieldName]?.find((obj) => obj && Object.keys(obj).length > 0);
  if (!nonEmptyObj) {
    latestValues[dataGroupFieldName] = [];
    removedDataGroupIndexes[dataGroupFieldName] = [];
  }

  setRemovedDataGroupIndexes(removedDataGroupIndexes);
  setCurrentValues(latestValues);
};

